import { useEffect, useState } from "react";
import {
  getProgressReportsList,
  getProgressReportDetail,
} from "../services/progressReportService";

export interface ProgressReportSummary {
  id: string;
  reportNumber: number;
  reportDate: string;
  status: string;
}

interface AiaItem {
  id: string;
  itemCode: number;
  itemName: string;
  itemLabel: string;
}

export interface LineItem {
  id: string;
  note: Record<string, string> | null;
  aiaItem: AiaItem;
  aiaItemId: string;
  isFlagged: boolean;
  sovAmount: number;
  gcBilledPct: number;
  totalAmount: { amount: number; currencyCode: string };
  cscEstimation: number;
  isIncludedFlagInReport: boolean;
}

export interface ReportDocument {
  id: string;
  type: string;
  description: string;
  isIncluded: boolean;
  note: string | null;
  document: { key: string; fileName: string; url: string };
}

interface DrawRequest {
  id: string;
  drawNumber: number;
  requestedAmount: {
    amount: number;
    currency: { code: string; symbol: string; name: string };
  };
  gcBilledPct: number;
}

export interface PhaseItem {
  id: string;
  note: Record<string, string> | null;
  itemCode: string;
  isFlagged: boolean;
  itemLabel: string;
  itemWeight: number;
  cscEstimation: number;
  isIncludedFlagInReport: boolean;
}

export interface Phase {
  phase: string;
  phaseOrder: number;
  phaseWeight: number;
  phaseCscEstimation: number;
  items: PhaseItem[];
}

export interface ProgressReportDetail {
  id: string;
  reportNumber: number;
  reportDate: string;
  inspectionDate: string | null;
  inspectorName: string | null;
  status: string;
  cscEstimation: string | null;
  invoiceAmount: { amount: number; currencyCode: string };
  reportBy: { firstName: string; lastName: string };
  drawRequest: DrawRequest | null;
  documents: ReportDocument[];
  inspectionSnapshot: { lineItems?: LineItem[]; phases?: Phase[] };
  inspectorNotes: { flags: string; general: string; offsite: string };
  bankActionItems: string;
  scheduleNotes: string;
  changeOrderNotes: string | null;
  recommendation: string;
  recommendationText: string;
  drawRequestId: string;
}

export function useProgressReports(projectId: string) {
  const [reports, setReports] = useState<ProgressReportSummary[]>([]);
  const [activeReportId, setActiveReportId] = useState<string>("");
  const [detail, setDetail] = useState<ProgressReportDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) return;
    const fetchList = async () => {
      setLoadingList(true);
      const { data, error: err } = await getProgressReportsList(projectId);
      if (err) {
        setError("Failed to load reports");
        setLoadingList(false);
        return;
      }
      const list: ProgressReportSummary[] = data?.data ?? [];
      setReports(list);
      if (list.length > 0) setActiveReportId(list[0].id);
      setLoadingList(false);
    };
    fetchList();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !activeReportId) return;
    const fetchDetail = async () => {
      setLoadingDetail(true);
      const { data, error: err } = await getProgressReportDetail(
        projectId,
        activeReportId,
      );
      if (err) {
        setError("Failed to load report detail");
        setLoadingDetail(false);
        return;
      }
      setDetail(data?.data ?? null);
      setLoadingDetail(false);
    };
    fetchDetail();
  }, [projectId, activeReportId]);

  return {
    reports,
    activeReportId,
    setActiveReportId,
    detail,
    loadingList,
    loadingDetail,
    error,
  };
}
